import { CATALOGO_OPERACIONES } from '../catalogos/catalogoOperaciones';

/**
 * MCM/MF del proceso PSA no tienen mapeo cerrado al catálogo PLD.
 * Valor por defecto hasta definir tabla de equivalencias con negocio.
 */
export const TIPO_OPERACION_PLD_DESDE_PROCESO = 'CONSTITUTIVA';

/** @param {string} [tipoProceso] mcm | mf */
export function mapProcesoMediacionTipoToTipoOperacionPld(tipoProceso) {
    void tipoProceso;
    return TIPO_OPERACION_PLD_DESDE_PROCESO;
}

function nuevoIdSeed(prefix) {
    return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
}

export function splitNombrePersona(full) {
    const s = (full || '').trim();
    if (!s) {
        return { nombre: '', paterno: '', materno: '' };
    }
    const parts = s.split(/\s+/);
    if (parts.length === 1) {
        return { nombre: parts[0], paterno: '', materno: '' };
    }
    if (parts.length === 2) {
        return { nombre: parts[0], paterno: parts[1], materno: '' };
    }
    return {
        nombre: parts[0],
        paterno: parts[1],
        materno: parts.slice(2).join(' '),
    };
}

export function mapSolicitanteToInitialState(solicitante) {
    if (!solicitante || !Object.keys(solicitante).length) {
        return null;
    }
    const nombreRaw = solicitante.nombre || '';
    let nombre;
    let paterno;
    let materno;
    if (solicitante.paterno || solicitante.materno) {
        nombre = nombreRaw;
        paterno = solicitante.paterno || '';
        materno = solicitante.materno || '';
    } else {
        const parts = splitNombrePersona(nombreRaw);
        nombre = parts.nombre;
        paterno = parts.paterno;
        materno = parts.materno;
    }

    return {
        nacionalidad: 'México',
        nombre,
        paterno,
        materno,
        rfc: solicitante.rfc ? String(solicitante.rfc).toUpperCase() : '',
        comparece: '',
        caracter: '',
    };
}

export function mapClienteToDenominacion(cliente) {
    if (!cliente) {
        return '';
    }
    return (cliente.nombre || '').trim();
}

function representanteFisicoDesdeLegal(nombreCompleto) {
    const parts = splitNombrePersona(nombreCompleto);
    return {
        id: nuevoIdSeed('rep'),
        rolBase: 'REPRESENTANTE LEGAL',
        tipo: 'Fisica',
        nacionalidad: 'México',
        nombre: parts.nombre,
        paterno: parts.paterno,
        materno: parts.materno,
        rfc: '',
    };
}

export function mapFirmanteToSocioRaiz(firmante, rolPorDefecto) {
    const tipoFirma = (firmante?.tipo || '').trim();
    const esMoral = tipoFirma === 'Persona moral';
    const base = {
        id: nuevoIdSeed('firm'),
        rolBase: rolPorDefecto,
        nacionalidad: 'México',
        acciones: '',
    };

    if (esMoral) {
        const rep =
            firmante.representanteLegal &&
            representanteFisicoDesdeLegal(firmante.representanteLegal);
        return {
            ...base,
            tipo: 'Moral',
            nombre: (firmante.nombre || '').trim(),
            rfc: firmante.rfc ? String(firmante.rfc).toUpperCase() : '',
            ...(rep ? { representante: rep } : {}),
        };
    }

    const parts = splitNombrePersona(firmante.nombre);
    return {
        ...base,
        tipo: 'Fisica',
        nombre: parts.nombre,
        paterno: parts.paterno,
        materno: parts.materno,
        rfc: firmante.rfc ? String(firmante.rfc).toUpperCase() : '',
    };
}

export function buildClienteParticipanteSeed(cliente, rolPorDefecto) {
    if (!cliente || !cliente.rfc) {
        return null;
    }
    const rfc = String(cliente.rfc).toUpperCase().trim();
    if (!rfc) {
        return null;
    }
    return {
        id: nuevoIdSeed('cli'),
        rolBase: rolPorDefecto,
        tipo: 'Moral',
        nacionalidad: 'México',
        nombre: (cliente.nombre || '').trim(),
        rfc,
        acciones: '',
        pldLegacyClienteId: String(cliente._id),
    };
}

export function mapFirmantesToSociosRaiz(firmantes, rolPorDefecto, rfcClienteUpper) {
    if (!Array.isArray(firmantes)) {
        return [];
    }
    const skipRfc = new Set();
    if (rfcClienteUpper) {
        skipRfc.add(rfcClienteUpper);
    }

    const out = [];
    for (const f of firmantes) {
        const socio = mapFirmanteToSocioRaiz(f, rolPorDefecto);
        const rfc = (socio.rfc || '').toUpperCase();
        if (rfc && skipRfc.has(rfc)) {
            continue;
        }
        if (rfc) {
            skipRfc.add(rfc);
        }
        out.push(socio);
    }
    return out;
}

/**
 * @param {{
 *   infoPreliminarDoc: object,
 *   clienteResuelto: object | null,
 *   firmantes: object[],
 *   tipoOperacionPld: string,
 * }} args
 */
export function buildSeedFromProceso({
    infoPreliminarDoc,
    clienteResuelto,
    firmantes,
    tipoOperacionPld,
}) {
    const roles = CATALOGO_OPERACIONES[tipoOperacionPld] || [];
    const rolPorDefecto = roles[0] || '';

    const asunto = (infoPreliminarDoc?.asunto || '').trim();
    const denominacion = mapClienteToDenominacion(clienteResuelto);
    const solicitanteInitial = mapSolicitanteToInitialState(
        infoPreliminarDoc?.solicitante
    );

    const rfcCliente = clienteResuelto?.rfc
        ? String(clienteResuelto.rfc).toUpperCase().trim()
        : '';

    const socios = [];
    const clienteSeed = buildClienteParticipanteSeed(
        clienteResuelto,
        rolPorDefecto
    );
    if (clienteSeed) {
        socios.push(clienteSeed);
    }

    const fromFirms = mapFirmantesToSociosRaiz(
        firmantes,
        rolPorDefecto,
        rfcCliente || null
    );
    socios.push(...fromFirms);

    return {
        asunto,
        tipoOperacion: tipoOperacionPld,
        denominacion,
        solicitanteInitial,
        sociosRaizInitial: socios,
    };
}
